<template>
  <div v-if="hasAccess">
    <PageHeader title="Préoccupations des salariés" />

    <PageContent>
      <section class="analysis-toolbar" aria-label="Analyse des préoccupations">
        <label class="toolbar-field toolbar-field--domain">
          <span>Domaine</span>
          <select v-model="selectedDomainUuid">
            <option value="">Tous les domaines</option>
            <option v-for="domain in availableDomains" :key="domain.uuid" :value="domain.uuid">
              {{ domain.label }}
            </option>
          </select>
        </label>

        <label class="toolbar-field">
          <span>Période à analyser</span>
          <select v-model="periodType">
            <option value="last_30_days">30 derniers jours</option>
            <option value="previous_month">Mois dernier</option>
          </select>
        </label>

        <button
          type="button"
          class="analysis-button"
          :disabled="!selectedDomainUuid || operationRunning || !manualRunEnabled"
          @click="runManualAnalysis"
        >
          <i class="fas fa-chart-line" aria-hidden="true"></i>
          {{ operationRunning ? 'Analyse en cours…' : 'Lancer une analyse' }}
        </button>

        <p
          v-if="operationMessage"
          class="operation-message"
          :class="{ 'operation-message--error': operationError }"
        >
          {{ operationMessage }}
        </p>
      </section>

      <div v-if="loading" class="concerns-state">Chargement des analyses…</div>
      <div v-else-if="error" class="concerns-state concerns-state--error">{{ error }}</div>
      <div v-else-if="rows.length === 0" class="concerns-state">
        Aucune analyse ne correspond à ce domaine.
      </div>

      <div v-else class="concerns-table-wrapper">
        <table class="concerns-table">
          <thead>
            <tr>
              <th class="expand-column"><span class="sr-only">Détails</span></th>
              <th>Période analysée</th>
              <th>Domaine</th>
              <th>Statut</th>
              <th>Sujet</th>
              <th>Catégorie</th>
              <th>Date de l’analyse</th>
              <th class="action-column">Action</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in rows">
              <tr
                :key="row.key"
                class="concern-row"
                :class="{ 'concern-row--expanded': isExpanded(row) }"
                tabindex="0"
                @click="toggleRow(row)"
                @keydown.enter.prevent="toggleRow(row)"
                @keydown.space.prevent="toggleRow(row)"
              >
                <td class="expand-column">
                  <i
                    class="fas"
                    :class="isExpanded(row) ? 'fa-chevron-up' : 'fa-chevron-down'"
                    aria-hidden="true"
                  ></i>
                </td>
                <td>
                  <strong>{{ periodLabel(row.run) }}</strong>
                  <small>{{ periodTypeLabel(row.run.period_type) }}</small>
                </td>
                <td>{{ row.run.domain.label }}</td>
                <td>
                  <span class="status-pill" :class="statusClass(row)">{{ statusLabel(row) }}</span>
                  <small v-if="row.concern">{{ decisionLabel(row.concern.decision) }}</small>
                </td>
                <td class="subject-cell">
                  <strong>{{ subjectLabel(row) }}</strong>
                  <small v-if="row.run.trigger === 'deepening'">Approfondissement demandé</small>
                </td>
                <td>{{ row.concern ? (row.concern.category || '—') : '—' }}</td>
                <td>{{ formatDateTime(row.run.generated_at || row.run.completed_at) }}</td>
                <td class="action-column" @click.stop @keydown.stop>
                  <button
                    v-if="row.concern && row.concern.can_deepen"
                    type="button"
                    class="deepen-button"
                    :disabled="operationRunning"
                    @click="deepenConcern(row.concern)"
                  >
                    <i class="fas fa-search-plus" aria-hidden="true"></i>
                    Approfondir
                  </button>
                  <span v-else>—</span>
                </td>
              </tr>

              <tr v-if="isExpanded(row)" :key="`${row.key}-details`" class="details-row">
                <td colspan="8">
                  <div v-if="row.concern" class="analysis-details">
                    <section class="analysis-level">
                      <header class="level-header">
                        <span class="level-number">1</span>
                        <div>
                          <h3>Ce qu’on observe</h3>
                        </div>
                      </header>

                      <div class="metrics-grid">
                        <article class="metric-card">
                          <span>Recherches sur le thème</span>
                          <strong>{{ observationMetrics(row.concern).searches }}</strong>
                          <small>sur la période analysée</small>
                        </article>
                        <article class="metric-card" :class="comparisonClass(row.concern)">
                          <span>Évolution des recherches</span>
                          <strong>{{ comparisonValue(row.concern) }}</strong>
                          <small>{{ comparisonCaption(row.concern) }}</small>
                        </article>
                        <article class="metric-card">
                          <span>Questions lues</span>
                          <strong>{{ observationMetrics(row.concern).question_reads }}</strong>
                          <small>consultations associées</small>
                        </article>
                        <article class="metric-card">
                          <span>Articles consultés</span>
                          <strong>{{ observationMetrics(row.concern).article_reads }}</strong>
                          <small>consultations associées</small>
                        </article>
                      </div>

                      <p class="privacy-note">
                        Les recherches et mots-clés affichés proviennent d’au moins trois salariés distincts.
                      </p>
                      <details class="signal-details">
                        <summary>Données utilisées pour cette observation</summary>
                        <div class="signal-grid">
                          <SignalList
                            title="Mots-clés les plus recherchés"
                            :items="signalItems(row.concern, 'keywords')"
                            empty-label="Aucun mot-clé suffisamment partagé"
                          />
                          <SignalList
                            title="Recherches les plus fréquentes"
                            :items="signalItems(row.concern, 'searches')"
                            empty-label="Aucune recherche suffisamment partagée"
                          />
                          <SignalList
                            title="Articles les plus lus"
                            :items="signalItems(row.concern, 'consulted_articles')"
                            empty-label="Aucune lecture d’article"
                          />
                          <SignalList
                            title="Questions les plus lues"
                            :items="signalItems(row.concern, 'consulted_faqs')"
                            empty-label="Aucune lecture de question"
                          />
                        </div>
                      </details>
                    </section>

                    <section class="analysis-level">
                      <header class="level-header">
                        <span class="level-number">2</span>
                        <div>
                          <h3>Besoin identifié et niveau de confiance</h3>
                        </div>
                      </header>

                      <div class="need-confidence-grid">
                        <article class="need-card">
                          <span>Besoin identifié</span>
                          <strong>{{ row.concern.problem || 'Besoin non précisé' }}</strong>
                          <small>{{ needMeta(row.concern) }}</small>
                        </article>
                        <article class="confidence-card" :class="confidenceClass(row.concern)">
                          <span>Niveau de confiance</span>
                          <strong>{{ confidenceLabel(row.concern) }}</strong>
                          <ul v-if="confidenceReasons(row.concern).length">
                            <li v-for="reason in confidenceReasons(row.concern)" :key="reason">{{ reason }}</li>
                          </ul>
                          <small v-else>Niveau indisponible pour cette ancienne analyse.</small>
                        </article>
                      </div>
                      <p v-if="row.concern.rationale" class="decision-rationale">
                        <strong>Lecture éditoriale :</strong> {{ row.concern.rationale }}
                      </p>
                    </section>

                    <section class="analysis-level">
                      <header class="level-header">
                        <span class="level-number">3</span>
                        <div>
                          <h3>Ce qu’on a déjà fait sur ce sujet</h3>
                        </div>
                      </header>

                      <div v-if="hasExistingCoverage(row.concern)" class="existing-coverage">
                        <div v-if="row.concern.existing_angles && row.concern.existing_angles.length" class="coverage-block">
                          <h4>Angles déjà couverts</h4>
                          <ul>
                            <li v-for="angle in row.concern.existing_angles" :key="angle">{{ angle }}</li>
                          </ul>
                        </div>
                        <div v-if="internalSourceList(row.concern).length" class="coverage-block">
                          <h4>Contenus existants examinés</h4>
                          <ul>
                            <li
                              v-for="(source, index) in internalSourceList(row.concern)"
                              :key="sourceKey(source, index)"
                            >
                              <span class="source-type">{{ sourceTypeLabel(source.type) }}</span>
                              <strong>{{ source.title || source.problem }}</strong>
                              <span v-if="source.date"> — {{ formatDate(source.date) }}</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <p v-else class="empty-coverage">Aucun contenu interne ne couvre précisément ce besoin.</p>
                    </section>

                    <section class="analysis-level">
                      <header class="level-header">
                        <span class="level-number">4</span>
                        <div>
                          <h3>Proposition pour combler le manque identifié</h3>
                        </div>
                      </header>

                      <div v-if="row.concern.status === 'proposed'" class="proposal-grid">
                        <article v-if="row.concern.proposed_title" class="proposal-title">
                          <span>Titre proposé</span>
                          <strong>{{ row.concern.proposed_title }}</strong>
                        </article>
                        <article v-if="row.concern.angle">
                          <span>Angle proposé</span>
                          <p>{{ row.concern.angle }}</p>
                        </article>
                        <article v-if="row.concern.content_brief">
                          <span>Brief éditorial</span>
                          <p>{{ row.concern.content_brief }}</p>
                        </article>
                      </div>
                      <p v-else class="empty-proposal">
                        Le besoin reste consultable et peut être approfondi manuellement si un nouvel angle paraît pertinent.
                      </p>

                      <div v-if="row.concern.web_sources && row.concern.web_sources.length" class="web-sources">
                        <h4>Sources Web utilisées pour étayer la proposition</h4>
                        <ul>
                          <li v-for="source in row.concern.web_sources" :key="source.url">
                            <a :href="source.url" target="_blank" rel="noopener noreferrer">
                              {{ source.title || source.url }}
                            </a>
                          </li>
                        </ul>
                      </div>
                    </section>
                  </div>

                  <div v-else class="empty-analysis-details">
                    Aucun sujet exploitable n’a été identifié à partir des signaux disponibles sur cette période.
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </PageContent>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageHeader from '@/components/PageHeader.vue'
import PageContent from '@/components/PageContent.vue'

const SignalList = {
  name: 'employee-concern-signal-list',
  props: {
    title: { type: String, required: true },
    items: { type: Array, default: () => [] },
    emptyLabel: { type: String, required: true }
  },
  methods: {
    metricLabel (item) {
      const occurrences = Number(item.occurrences || 0)
      const users = Number(item.distinct_users || 0)
      const occurrenceLabel = `${occurrences} occurrence${occurrences > 1 ? 's' : ''}`
      if (!users) return occurrenceLabel
      return `${occurrenceLabel} · ${users} salarié${users > 1 ? 's' : ''}`
    }
  },
  template: `
    <article class="signal-card">
      <h4>{{ title }}</h4>
      <ul v-if="items.length">
        <li v-for="(item, index) in items" :key="item.title + '-' + index">
          <strong>{{ item.title }}</strong>
          <small>{{ metricLabel(item) }}</small>
        </li>
      </ul>
      <p v-else class="signal-empty">{{ emptyLabel }}</p>
    </article>
  `
}

export default {
  name: 'app-dashboard-employee-concerns',
  components: { PageHeader, PageContent, SignalList },
  data () {
    return {
      analysisRuns: [],
      availableDomains: [],
      selectedDomainUuid: '',
      periodType: 'last_30_days',
      expandedRowKey: '',
      loading: false,
      error: '',
      manualRunEnabled: true,
      operationRunning: false,
      operationRunId: '',
      operationMessage: '',
      operationError: false,
      operationPollAttempts: 0,
      operationPollTimer: null
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.some(role => ['admin', 'community-manager'].includes(role))
    },
    filteredRuns () {
      if (!this.selectedDomainUuid) return this.analysisRuns
      return this.analysisRuns.filter(run => run.domain && run.domain.uuid === this.selectedDomainUuid)
    },
    rows () {
      return this.filteredRuns.reduce((rows, run) => {
        if (!Array.isArray(run.concerns) || run.concerns.length === 0) {
          rows.push({ key: `${run.uuid}-empty`, run, concern: null })
          return rows
        }
        run.concerns.forEach(concern => {
          rows.push({ key: `${run.uuid}-${concern.uuid}`, run, concern })
        })
        return rows
      }, [])
    }
  },
  watch: {
    selectedDomainUuid () {
      this.expandedRowKey = ''
      this.loadAnalyses()
    }
  },
  async created () {
    if (!Array.isArray(this.roles) || this.roles.length === 0) {
      await this.$store.dispatch('FETCH_ROLES')
    }
    if (!this.hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }
    this.loadAnalyses()
  },
  beforeDestroy () {
    if (this.operationPollTimer) window.clearTimeout(this.operationPollTimer)
  },
  methods: {
    async loadAnalyses ({ silent = false } = {}) {
      if (!silent) {
        this.loading = true
        this.error = ''
      }
      try {
        const params = { limit: 500 }
        if (this.selectedDomainUuid) params.domainUuid = this.selectedDomainUuid
        const { data } = await http.get('/dashboard/employee-concerns', { params })
        this.analysisRuns = Array.isArray(data.analysis_runs) ? data.analysis_runs : []
        this.availableDomains = Array.isArray(data.available_domains) ? data.available_domains : []
        this.manualRunEnabled = data.manual_run_enabled === true
      } catch (err) {
        console.log(err)
        if (!silent) this.error = err?.response?.data?.message || 'Impossible de charger les analyses.'
      } finally {
        if (!silent) this.loading = false
      }
    },
    async runManualAnalysis () {
      const domain = this.availableDomains.find(item => item.uuid === this.selectedDomainUuid)
      if (!domain) return
      const period = this.periodType === 'last_30_days' ? 'les 30 derniers jours' : 'le mois dernier'
      if (!window.confirm(`Lancer l’analyse de ${period} pour « ${domain.label} » ?`)) return

      await this.startOperation(
        () => http.post('/dashboard/employee-concerns/runs', {
          domainUuid: domain.uuid,
          periodType: this.periodType
        }),
        'Ajout de l’analyse à la file d’attente…'
      )
    },
    async deepenConcern (concern) {
      if (!window.confirm('Approfondir ce sujet et ajouter une nouvelle proposition éditoriale ?')) return
      await this.startOperation(
        () => http.post(`/dashboard/employee-concerns/${concern.uuid}/deepen`),
        'Ajout de l’approfondissement à la file d’attente…'
      )
    },
    async startOperation (request, pendingMessage) {
      this.operationRunning = true
      this.operationError = false
      this.operationMessage = pendingMessage
      this.operationPollAttempts = 0
      try {
        const { data } = await request()
        this.operationRunId = data.run.id
        this.operationMessage = data.run.message
        this.scheduleOperationPoll(1500)
      } catch (err) {
        console.log(err)
        this.operationRunning = false
        this.operationError = true
        this.operationMessage = err?.response?.data?.message || 'Impossible de lancer cette opération.'
      }
    },
    scheduleOperationPoll (delay = 3000) {
      if (this.operationPollTimer) window.clearTimeout(this.operationPollTimer)
      this.operationPollTimer = window.setTimeout(() => this.pollOperation(), delay)
    },
    async pollOperation () {
      if (!this.operationRunId) return
      this.operationPollAttempts += 1
      try {
        const { data } = await http.get(`/dashboard/employee-concerns/runs/${this.operationRunId}`)
        const run = data.run || {}
        this.operationMessage = run.message || 'Analyse en cours…'
        if (['queued', 'running'].includes(run.status)) {
          if (this.operationPollAttempts >= 120) {
            this.operationRunning = false
            this.operationError = true
            this.operationMessage = 'L’analyse prend plus de temps que prévu. Revenez dans quelques minutes.'
            return
          }
          this.scheduleOperationPoll()
          return
        }

        this.operationRunning = false
        this.operationError = run.status !== 'completed'
        if (run.status === 'completed') await this.loadAnalyses({ silent: true })
      } catch (err) {
        console.log(err)
        this.operationRunning = false
        this.operationError = true
        this.operationMessage = err?.response?.data?.message || 'Impossible de suivre cette opération.'
      }
    },
    toggleRow (row) {
      this.expandedRowKey = this.isExpanded(row) ? '' : row.key
    },
    isExpanded (row) {
      return this.expandedRowKey === row.key
    },
    statusLabel (row) {
      if (row.run.status === 'failed') return 'Échec'
      if (!row.concern) {
        return {
          queued: 'En attente',
          running: 'En cours',
          failed: 'Échec',
          completed: 'Aucun sujet'
        }[row.run.status] || 'Aucun sujet'
      }
      if (row.concern.decision === 'budget_limit') return 'Différée'
      return row.concern.status === 'proposed' ? 'Retenue' : 'Rejetée'
    },
    statusClass (row) {
      if (row.run.status === 'failed') return 'status-pill--failed'
      if (!row.concern) return `status-pill--${row.run.status || 'empty'}`
      if (row.concern.decision === 'budget_limit') return 'status-pill--queued'
      return row.concern.status === 'proposed' ? 'status-pill--proposed' : 'status-pill--skipped'
    },
    subjectLabel (row) {
      if (!row.concern) {
        if (row.run.status === 'failed') return 'L’analyse a échoué'
        if (['queued', 'running'].includes(row.run.status)) return 'Analyse en cours'
        return 'Aucun signal exploitable'
      }
      return row.concern.problem || row.concern.proposed_title || 'Sujet sans titre'
    },
    decisionLabel (decision) {
      return {
        new_topic: 'Nouveau sujet',
        different_angle: 'Nouvel angle',
        deeper_angle: 'Approfondissement',
        insufficient_value: 'Valeur ajoutée insuffisante',
        budget_limit: 'Analyse différée — plafond atteint'
      }[decision] || 'Analyse éditoriale'
    },
    periodLabel (run) {
      return `${this.formatDate(run.period_start)} – ${this.formatDate(run.period_end)}`
    },
    periodTypeLabel (type) {
      return {
        last_30_days: '30 derniers jours',
        previous_month: 'Mois dernier',
        legacy: 'Analyse historique',
        custom: 'Période personnalisée'
      }[type] || 'Période analysée'
    },
    formatDate (date) {
      if (!date) return '—'
      return new Intl.DateTimeFormat('fr-FR').format(new Date(`${String(date).slice(0, 10)}T12:00:00`))
    },
    formatDateTime (date) {
      if (!date) return '—'
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date))
    },
    signalItems (concern, key) {
      const signals = concern.signals || {}
      return Array.isArray(signals[key]) ? signals[key] : []
    },
    observationMetrics (concern) {
      const metrics = concern.observation && concern.observation.metrics
      if (metrics) {
        return {
          searches: Number(metrics.searches || 0),
          question_reads: Number(metrics.question_reads || 0),
          article_reads: Number(metrics.article_reads || 0),
          total_reads: Number(metrics.total_reads || 0)
        }
      }

      const occurrenceSum = key => this.signalItems(concern, key)
        .reduce((total, item) => total + Number(item.occurrences || 0), 0)
      const keywordMaximum = this.signalItems(concern, 'keywords')
        .reduce((maximum, item) => Math.max(maximum, Number(item.occurrences || 0)), 0)
      const questionReads = occurrenceSum('consulted_faqs')
      const articleReads = occurrenceSum('consulted_articles')
      return {
        searches: Math.max(occurrenceSum('searches'), keywordMaximum),
        question_reads: questionReads,
        article_reads: articleReads,
        total_reads: questionReads + articleReads
      }
    },
    comparison (concern) {
      return (concern.observation && concern.observation.comparison) || { available: false }
    },
    comparisonValue (concern) {
      const comparison = this.comparison(concern)
      if (!comparison.available) return '—'
      if (comparison.search_change_state === 'new') return 'Nouveau'
      const percentage = Number(comparison.search_change_percent || 0)
      return `${percentage > 0 ? '+' : ''}${percentage} %`
    },
    comparisonCaption (concern) {
      const comparison = this.comparison(concern)
      if (!comparison.available) return 'Comparaison indisponible'
      return `vs ${this.formatDate(comparison.period_start)} – ${this.formatDate(comparison.period_end)}`
    },
    comparisonClass (concern) {
      const comparison = this.comparison(concern)
      if (!comparison.available) return 'metric-card--muted'
      return `metric-card--${comparison.search_change_state || 'stable'}`
    },
    confidence (concern) {
      return concern.confidence && concern.confidence.level
        ? concern.confidence
        : { level: 'unknown', reasons: [] }
    },
    confidenceLabel (concern) {
      return {
        strong: 'Opportunité forte',
        medium: 'Opportunité moyenne',
        low: 'Opportunité faible',
        unknown: 'Confiance indisponible'
      }[this.confidence(concern).level]
    },
    confidenceReasons (concern) {
      const reasons = this.confidence(concern).reasons
      return Array.isArray(reasons) ? reasons : []
    },
    confidenceClass (concern) {
      return `confidence--${this.confidence(concern).level}`
    },
    needMeta (concern) {
      const labels = [concern.theme, concern.category].filter(label => Boolean(label))
      return labels.join(' · ') || 'Catégorie non précisée'
    },
    hasExistingCoverage (concern) {
      return (Array.isArray(concern.existing_angles) && concern.existing_angles.length > 0) ||
        this.internalSourceList(concern).length > 0
    },
    internalSourceList (concern) {
      const sources = concern.internal_sources || {}
      const seenArticles = new Set()
      return [
        ...(sources.articles || []),
        ...(sources.faqs || [])
      ].filter(source => {
        if (source.type !== 'article') return true
        const normalizedTitle = String(source.title || '').trim().toLocaleLowerCase('fr')
        const key = normalizedTitle || source.uuid
        if (!key || seenArticles.has(key)) return false
        seenArticles.add(key)
        return true
      }).slice(0, 18)
    },
    sourceKey (source, index) {
      return `${source.type || 'source'}-${source.uuid || source.title || source.problem}-${source.date || ''}-${index}`
    },
    sourceTypeLabel (type) {
      return { article: 'Article', faq: 'Question' }[type] || 'Source'
    }
  }
}
</script>

<style scoped>
.analysis-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 1rem 1.15rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.8rem;
  background: #fff;
}

.toolbar-field {
  display: flex;
  min-width: 13rem;
  color: #111827;
  font-size: 0.875rem;
  flex-direction: column;
  gap: 0.35rem;
}

.toolbar-field--domain {
  min-width: 20rem;
  flex: 1;
}

.toolbar-field span {
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.toolbar-field select {
  height: 2.65rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  background: #fff;
  padding: 0 0.75rem;
  color: #111827;
}

.analysis-button,
.deepen-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: 0;
  border-radius: 0.55rem;
  background: #2563eb;
  color: #fff;
  font-weight: 400;
  cursor: pointer;
}

.analysis-button {
  min-height: 2.65rem;
  padding: 0 1.1rem;
}

.analysis-button:disabled,
.deepen-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.operation-message {
  flex-basis: 100%;
  margin: 0;
  color: #2563eb;
  font-size: 0.75rem;
}

.operation-message--error,
.concerns-state--error {
  color: #b91c1c;
}

.concerns-state {
  padding: 3rem 1rem;
  color: #4b5563;
  text-align: center;
}

.concerns-table-wrapper {
  overflow-x: auto;
  border: 1px solid #bfdbfe;
  border-radius: 0.8rem;
  background: #fff;
}

.concerns-table {
  width: 100%;
  border-collapse: collapse;
  color: #111827;
}

.concerns-table th {
  padding: 0.85rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.035em;
  text-align: left;
  text-transform: uppercase;
}

.concern-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.concern-row:hover,
.concern-row:focus,
.concern-row--expanded {
  background: #f9fafb;
  outline: none;
}

.concern-row td {
  min-width: 8rem;
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
}

.concern-row td small,
.subject-cell small {
  display: block;
  margin-top: 0.2rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.subject-cell {
  min-width: 17rem !important;
}

.expand-column {
  width: 2.5rem;
  min-width: 2.5rem !important;
  text-align: center !important;
}

.action-column {
  width: 9rem;
  min-width: 9rem !important;
  text-align: center !important;
}

.deepen-button {
  padding: 0.55rem 0.75rem;
  font-size: 0.875rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.3rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pill--proposed,
.status-pill--completed {
  background: #dcfce7;
  color: #166534;
}

.status-pill--skipped,
.status-pill--failed {
  background: #fee2e2;
  color: #991b1b;
}

.status-pill--queued,
.status-pill--running {
  background: #fef3c7;
  color: #92400e;
}

.details-row > td {
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.analysis-details,
.empty-analysis-details {
  padding: 1.25rem 1.5rem 1.5rem 3.25rem;
}

.empty-analysis-details {
  color: #4b5563;
}

.analysis-level {
  position: relative;
  margin-bottom: 1rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  background: #fff;
  padding: 1.1rem;
}

.analysis-level:last-child {
  margin-bottom: 0;
}

.analysis-details h3,
.analysis-details h4 {
  margin: 0 0 0.55rem;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
}

.analysis-details p {
  margin: 0;
  white-space: pre-line;
}

.level-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.level-header h3 {
  margin: 0 0 0.15rem;
  font-size: 1rem;
}

.level-header p {
  color: #4b5563;
  font-size: 0.75rem;
}

.level-number {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  flex: 0 0 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.metric-card,
.need-card,
.confidence-card,
.proposal-grid > article,
.coverage-block {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 0.6rem;
  background: #f9fafb;
  padding: 0.85rem;
}

.metric-card > span,
.need-card > span,
.confidence-card > span,
.proposal-grid > article > span {
  display: block;
  margin-bottom: 0.35rem;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}

.metric-card > strong {
  display: block;
  color: #111827;
  font-size: 1.45rem;
  line-height: 1.2;
}

.metric-card > small,
.need-card > small,
.confidence-card > small {
  display: block;
  margin-top: 0.2rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.metric-card--up,
.metric-card--new {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.metric-card--down {
  border-color: #fed7aa;
  background: #fff7ed;
}

.metric-card--muted {
  color: #4b5563;
}

.confidence--strong {
  border-color: #86efac;
  background: #f0fdf4;
  color: #166534;
}

.confidence--medium {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.confidence--low {
  border-color: #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.confidence--unknown {
  background: #f9fafb;
  color: #4b5563;
}

.privacy-note {
  margin: 0.85rem 0 !important;
  color: #4b5563;
  font-size: 0.75rem;
}

.signal-details {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.75rem;
}

.signal-details summary {
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.signal-details[open] summary {
  margin-bottom: 0.85rem;
}

.signal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

::v-deep .signal-card {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  padding: 0.85rem;
}

.need-confidence-grid,
.proposal-grid,
.existing-coverage {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.need-card > strong,
.confidence-card > strong,
.proposal-grid > article > strong {
  display: block;
  color: inherit;
  font-size: 0.875rem;
}

.confidence-card ul,
.coverage-block ul,
.web-sources ul {
  margin: 0.65rem 0 0;
  padding-left: 1.1rem;
}

.confidence-card li,
.coverage-block li,
.web-sources li {
  margin: 0.35rem 0;
  font-size: 0.75rem;
}

.decision-rationale {
  margin-top: 0.85rem !important;
  border-left: 3px solid #bfdbfe;
  padding-left: 0.75rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.coverage-block h4,
.web-sources h4 {
  margin-bottom: 0.65rem;
}

.empty-coverage,
.empty-proposal {
  border-radius: 0.55rem;
  background: #f9fafb;
  padding: 0.85rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.proposal-title {
  grid-column: 1 / -1;
  border-color: #bfdbfe !important;
  background: #eff6ff !important;
}

.web-sources {
  margin-top: 0.9rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 0.9rem;
}

::v-deep .signal-card ul {
  margin: 0;
  padding-left: 1.1rem;
}

::v-deep .signal-card li {
  margin: 0.4rem 0;
}

::v-deep .signal-card strong,
::v-deep .signal-card small {
  display: block;
}

::v-deep .signal-card small,
::v-deep .signal-empty {
  margin-top: 0.12rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.source-type {
  display: inline-block;
  min-width: 6rem;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .analysis-toolbar {
    flex-wrap: wrap;
  }

  .toolbar-field--domain {
    min-width: 16rem;
  }
}

@media (max-width: 760px) {
  .toolbar-field,
  .toolbar-field--domain,
  .analysis-button {
    width: 100%;
    min-width: 0;
  }

  .signal-grid,
  .metrics-grid,
  .need-confidence-grid,
  .proposal-grid,
  .existing-coverage {
    grid-template-columns: 1fr;
  }

  .proposal-title {
    grid-column: auto;
  }

  .signal-summary {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
